<template>
  <div class="ai-simple-prompt-picker">
    <label class="ai-form-question">
      What should the script say?
      <b-badge v-if="isOther && optional" variant="light-secondary" class="ml-50 ai-optional-badge align-middle">
        Optional
      </b-badge>
    </label>
    <p class="ai-form-hint">
      {{
        isOther
          ? 'Optional — describe the goal, or skip and we’ll use the standard opening.'
          : 'This is what the AI will say first. Change the opening line only if you need to.'
      }}
    </p>

    <div v-if="isOther" class="mb-1">
      <label class="ai-sub-label d-block mb-50">Describe this call</label>
      <b-form-textarea
        :value="otherBrief"
        rows="3"
        :disabled="disabled"
        placeholder="e.g. Follow up on offer acceptance, confirm joining date"
        class="ai-script-input"
        @input="onOtherBriefInput"
      />
    </div>

    <div v-if="generating" class="ai-generating-hint mb-1">
      <b-spinner small class="mr-50" />
      Updating the script…
    </div>

    <transition name="ai-setup-reveal">
      <div v-if="showPreview && previewOpening && !generating" class="ai-opening-preview mb-1">
        <div class="ai-opening-preview__header">
          <feather-icon icon="MessageCircleIcon" size="16" class="text-primary mr-50" />
          <span class="ai-opening-preview__title">Here’s how it opens</span>
          <b-badge v-if="languageHint" variant="light-primary" class="ai-language-badge ml-auto">
            {{ languageHint }}
          </b-badge>
        </div>
        <blockquote class="ai-opening-preview__quote mb-0">
          {{ previewOpening }}
        </blockquote>
      </div>
    </transition>

    <template v-if="!isOther">
      <b-button
        variant="link"
        class="ai-flavor-toggle w-100 text-left"
        :disabled="disabled"
        @click="flavorsOpen = !flavorsOpen"
      >
        <feather-icon
          :icon="flavorsOpen ? 'ChevronDownIcon' : 'ChevronRightIcon'"
          size="16"
          class="mr-50 flex-shrink-0 text-muted"
        />
        <span>{{ flavorsOpen ? 'Hide opening line options' : 'Change opening line' }}</span>
        <span v-if="!flavorsOpen && selectedFlavorLabel" class="ai-flavor-toggle__current">
          {{ selectedFlavorLabel }}
        </span>
      </b-button>
      <b-collapse v-model="flavorsOpen">
        <div class="ai-prompt-chips mb-1">
          <button
            v-for="preset in presets"
            :key="preset.id"
            type="button"
            class="ai-prompt-chip"
            :class="{ 'ai-prompt-chip--active': selectedPresetId === preset.id }"
            :disabled="disabled"
            :title="preset.description"
            @click="selectPreset(preset.id)"
          >
            <span class="ai-prompt-chip__label">{{ preset.label }}</span>
            <span v-if="preset.description" class="ai-prompt-chip__desc">{{ preset.description }}</span>
          </button>
          <button
            type="button"
            class="ai-prompt-chip"
            :class="{ 'ai-prompt-chip--active': selectedPresetId === 'custom' }"
            :disabled="disabled"
            title="Add a one-line customization"
            @click="selectPreset('custom')"
          >
            <span class="ai-prompt-chip__label">Custom line</span>
            <span class="ai-prompt-chip__desc">Your own one-line focus</span>
          </button>
        </div>

        <div v-if="selectedPresetId === 'custom'" class="mb-1">
          <label class="ai-sub-label d-block mb-50">Your one-line prompt</label>
          <b-form-input
            :value="customBrief"
            :disabled="disabled"
            placeholder="e.g. Focus on night-shift availability and transport"
            class="ai-script-input"
            @input="onCustomBriefInput"
          />
        </div>
      </b-collapse>
    </template>

    <div v-if="!hasScriptChoice && !optional" class="ai-setup-next-hint text-muted mb-0">
      {{ isOther ? 'Add a short description to continue.' : 'Select a script to continue.' }}
    </div>
  </div>
</template>

<script>
import { BFormTextarea, BFormInput, BSpinner, BBadge, BButton, BCollapse } from 'bootstrap-vue'
import {
  AI_CALL_TYPES,
  getPresetPromptLines,
  normalizeCallType,
} from '@/utils/ai-calling-prompt-defaults'

export default {
  name: 'AiCallingSimplePromptPicker',
  components: { BFormTextarea, BFormInput, BSpinner, BBadge, BButton, BCollapse },
  props: {
    callType: { type: String, default: 'fresh' },
    selectedPresetId: { type: String, default: '' },
    customBrief: { type: String, default: '' },
    otherBrief: { type: String, default: '' },
    previewOpening: { type: String, default: '' },
    languageHint: { type: String, default: '' },
    generating: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    optional: { type: Boolean, default: true },
    showPreview: { type: Boolean, default: true },
  },
  data() {
    return {
      flavorsOpen: this.selectedPresetId === 'custom',
    }
  },
  computed: {
    isOther() {
      return normalizeCallType(this.callType) === AI_CALL_TYPES.OTHER
    },
    presets() {
      return getPresetPromptLines(this.callType)
    },
    hasScriptChoice() {
      if (this.isOther) {
        return !!(this.otherBrief || '').trim()
      }
      return !!(this.selectedPresetId || '').trim()
    },
    selectedFlavorLabel() {
      if (this.selectedPresetId === 'custom') {
        const brief = (this.customBrief || '').trim()
        return brief || 'Custom line'
      }
      const preset = this.presets.find(p => p.id === this.selectedPresetId)
      return preset ? preset.label : ''
    },
  },
  watch: {
    selectedPresetId(id) {
      if (id === 'custom') this.flavorsOpen = true
    },
  },
  methods: {
    selectPreset(id) {
      this.$emit('update:selectedPresetId', id)
      this.$emit('change')
    },
    onCustomBriefInput(val) {
      this.$emit('update:customBrief', val)
      this.$emit('change')
    },
    onOtherBriefInput(val) {
      this.$emit('update:otherBrief', val)
      this.$emit('change')
    },
  },
}
</script>

<style scoped>
.ai-simple-prompt-picker {
  --ai-brand: #00ac99;
  --ai-brand-soft: #e6f7f5;
  --ai-brand-border: rgba(0, 172, 153, 0.45);
}
.ai-form-question {
  display: block;
  font-size: 1.0625rem;
  font-weight: 700;
  color: #2f2b3d;
  line-height: 1.35;
  margin-bottom: 0.3rem;
}
.ai-form-hint {
  font-size: 0.875rem;
  line-height: 1.45;
  color: #5e5873;
  margin-bottom: 0.75rem;
}
.ai-sub-label {
  font-size: 14px;
  font-weight: 600;
  color: #2f2b3d;
}
.ai-setup-next-hint {
  font-size: 14px;
  padding: 0.65rem 0.8rem;
  border-radius: 0.5rem;
  background: var(--ai-brand-soft);
  border: 1px dashed var(--ai-brand-border);
  color: #4b465c;
}
.ai-flavor-toggle {
  color: inherit;
  text-decoration: none;
  border: none;
  border-radius: 0;
  padding: 0.45rem 0;
  background: transparent;
  font-size: 15px;
  font-weight: 600;
}
.ai-flavor-toggle:hover:not(:disabled),
.ai-flavor-toggle:focus:not(:disabled) {
  color: var(--ai-brand);
  text-decoration: none;
  box-shadow: none;
}
.ai-flavor-toggle__current {
  margin-left: 0.45rem;
  font-weight: 500;
  color: #6e6b7b;
}
.ai-prompt-chips {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.35rem;
}
@media (min-width: 576px) {
  .ai-prompt-chips {
    flex-direction: row;
    flex-wrap: wrap;
  }
  .ai-prompt-chip {
    flex: 1 1 calc(50% - 0.4rem);
    min-width: 11rem;
  }
}
.ai-prompt-chip {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
  text-align: left;
  min-height: 0;
  padding: 0.65rem 0.8rem;
  border: 1.5px solid rgba(47, 43, 61, 0.12);
  border-radius: 0.45rem;
  background: #fff;
  color: #4b465c;
  cursor: pointer;
  box-shadow: none;
}
.ai-prompt-chip__label {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.25;
}
.ai-prompt-chip__desc {
  font-size: 13px;
  font-weight: 400;
  line-height: 1.35;
  color: #6e6b7b;
}
.ai-prompt-chip:hover:not(:disabled):not(.ai-prompt-chip--active) {
  background: var(--ai-brand-soft);
  border-color: rgba(0, 172, 153, 0.4);
  color: #007a6e;
}
.ai-prompt-chip:hover:not(:disabled):not(.ai-prompt-chip--active) .ai-prompt-chip__desc {
  color: rgba(0, 140, 126, 0.9);
}
.ai-prompt-chip:focus {
  outline: none;
  box-shadow: none;
}
.ai-prompt-chip:focus-visible {
  outline: 2px solid var(--ai-brand);
  outline-offset: 2px;
}
.ai-prompt-chip--active {
  background: var(--ai-brand-soft);
  color: #007a6e;
  border-color: var(--ai-brand);
  box-shadow: 0 0 0 1px var(--ai-brand);
}
.ai-prompt-chip--active .ai-prompt-chip__label {
  font-weight: 700;
}
.ai-prompt-chip--active .ai-prompt-chip__desc {
  color: #4a8a84;
}
.ai-prompt-chip:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.ai-script-input {
  font-size: 15px;
  line-height: 1.45;
  min-height: 2.5rem;
}
.ai-generating-hint {
  display: flex;
  align-items: center;
  padding: 0.75rem 0.9rem;
  border-radius: 0.5rem;
  background: var(--ai-brand-soft);
  color: #4b465c;
  font-size: 14px;
}
.ai-opening-preview {
  border: 1px solid rgba(47, 43, 61, 0.12);
  border-left: 3px solid var(--ai-brand);
  border-radius: 0.5rem;
  background: #fff;
  overflow: hidden;
  box-shadow: none;
}
.ai-opening-preview__header {
  display: flex;
  align-items: center;
  padding: 0.65rem 0.9rem;
  background: #f8f8f8;
  border-bottom: 1px solid rgba(47, 43, 61, 0.12);
}
.ai-opening-preview__title {
  font-size: 15px;
  font-weight: 700;
  color: #2f2b3d;
}
.ai-language-badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.2rem 0.5rem;
}
.ai-optional-badge {
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.15rem 0.4rem;
}
.ai-opening-preview__quote {
  margin: 0;
  padding: 0.95rem 1rem;
  font-size: 15px;
  line-height: 1.55;
  color: #4b465c;
  white-space: pre-wrap;
  word-break: break-word;
  font-style: normal;
  border-left: none;
}
.ai-setup-reveal-enter-active,
.ai-setup-reveal-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.ai-setup-reveal-enter,
.ai-setup-reveal-leave-to {
  opacity: 0;
  transform: translateY(0.35rem);
}
</style>
